import React, { useState, useEffect } from 'react';
import { ClipboardList, BookOpen, Monitor, SlidersHorizontal, LogOut } from 'lucide-react';
import { LoginPage } from './pages/LoginPage';
import { OrdersBoard } from './pages/OrdersBoard';
import { MenuManagerPage } from './pages/MenuManagerPage';
import { KioskOrderPage } from './pages/KioskOrderPage';
import { SettingsPage } from './pages/SettingsPage';
import { AdminDashboard } from './pages/AdminDashboard';
import { AuthState } from './types';
import { getStoredSession, signOut, signIn } from './lib/cognito';

type Tab = 'orders' | 'menu' | 'kiosk' | 'settings';

function LogoutModal({ username, onConfirm, onCancel }: { username: string; onConfirm: () => void; onCancel: () => void }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(username, password);
      onConfirm();
    } catch {
      setError('Incorrect password.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onCancel}>
      <div className="bg-gray-900 border border-white/10 rounded-2xl p-6 w-full max-w-sm shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-white font-bold text-lg mb-1">Confirm Sign Out</h3>
        <p className="text-gray-400 text-sm mb-5">Enter your password to sign out.</p>
        <form onSubmit={handleSubmit} className="space-y-4">
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            required
            autoFocus
            autoComplete="current-password"
            className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500/50 focus:border-red-500/50 text-sm"
          />
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <div className="flex gap-3">
            <button type="button" onClick={onCancel} className="flex-1 py-2.5 rounded-xl text-sm font-semibold text-gray-400 bg-white/5 hover:bg-white/10 transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-red-600 hover:bg-red-500 transition-colors disabled:opacity-60">
              {loading ? 'Verifying...' : 'Sign Out'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const App: React.FC = () => {
  const [auth, setAuth] = useState<AuthState>({
    isAuthenticated: false,
    restaurantId: '',
    restaurantName: '',
  });
  const [activeTab, setActiveTab] = useState<Tab>('orders');
  const [sessionLoading, setSessionLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [username, setUsername] = useState('');

  // Restore Cognito session on page load
  useEffect(() => {
    getStoredSession().then((result) => {
      if (result) {
        setAuth({
          isAuthenticated: true,
          restaurantId:   result.restaurantId,
          restaurantName: result.restaurantName,
          role:           result.role,
        });
        const payload = result.session.getIdToken().decodePayload();
        setUsername((payload['cognito:username'] as string) || '');
      }
    }).finally(() => setSessionLoading(false));
  }, []);

  if (sessionLoading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-4 border-indigo-600 border-t-transparent animate-spin" />
      </div>
    );
  }

  const handleLogin = (authState: AuthState & { username?: string }) => {
    setAuth(authState);
    if (authState.username) setUsername(authState.username);
  };
  const handleLogout = () => {
    signOut();
    setAuth({ isAuthenticated: false, restaurantId: '', restaurantName: '' });
    setActiveTab('orders');
    setShowLogoutModal(false);
  };

  if (!auth.isAuthenticated) {
    return <LoginPage onLogin={handleLogin} />;
  }

  const isAdmin = auth.role === 'admin';

  if (isAdmin) {
    return (
      <div className="min-h-screen bg-gray-950 flex flex-col">
        <nav className="flex items-center gap-2 px-6 py-3 bg-gray-900 border-b border-white/8">
          <div className="flex items-center gap-3 mr-auto">
            <div className="w-8 h-8 rounded-lg bg-purple-600 flex items-center justify-center font-black text-white text-sm">A</div>
            <span className="text-white font-bold text-sm hidden sm:block">Platform Admin</span>
            <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-300 text-xs font-semibold">ADMIN</span>
          </div>
          <button
            onClick={() => setShowLogoutModal(true)}
            className="ml-4 text-gray-500 hover:text-red-400 text-sm font-semibold transition-colors"
          >
            Sign Out
          </button>
        </nav>
        {showLogoutModal && (
          <LogoutModal username={username} onConfirm={handleLogout} onCancel={() => setShowLogoutModal(false)} />
        )}
        <div className="flex-1 flex flex-col">
          <AdminDashboard />
          <div className="py-2 text-center">
            <span className="text-[10px] text-gray-600">Powered by AccTech Kiosk</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      {/* ── Top Nav ─────────────────────────────────────────────────── */}
      <nav className="flex items-center gap-2 px-6 py-3 bg-gray-900 border-b border-white/8">
        <div className="flex items-center gap-3 mr-auto">
          <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center font-black text-white text-sm">K</div>
          <span className="text-white font-bold text-sm hidden sm:block">{auth.restaurantName}</span>
        </div>

        {/* Tab buttons */}
        <div className="flex bg-white/5 rounded-xl p-1 gap-1">
          {([
            { key: 'orders', label: 'Orders', icon: ClipboardList },
            { key: 'menu', label: 'Menu', icon: BookOpen },
            { key: 'kiosk', label: 'Kiosk', icon: Monitor },
            { key: 'settings', label: 'Settings', icon: SlidersHorizontal },
          ] as const).map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              className={`flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === key
                  ? 'bg-indigo-600 text-white shadow'
                  : 'text-gray-400 hover:text-white'
              }`}
            >
              <Icon size={14} strokeWidth={2.5} />
              {label}
            </button>
          ))}
        </div>

        <button
          onClick={() => setShowLogoutModal(true)}
          className="ml-4 flex items-center gap-1.5 text-gray-500 hover:text-red-400 text-sm font-semibold transition-colors"
        >
          <LogOut size={14} strokeWidth={2} />
          Sign Out
        </button>
      </nav>

      {showLogoutModal && (
        <LogoutModal
          username={username}
          onConfirm={handleLogout}
          onCancel={() => setShowLogoutModal(false)}
        />
      )}

      {/* ── Page Content ────────────────────────────────────────────── */}
      <div className="flex-1 flex flex-col">
        {activeTab === 'orders' ? (
          <OrdersBoard
            restaurantId={auth.restaurantId}
            restaurantName={auth.restaurantName}
            onLogout={handleLogout}
          />
        ) : activeTab === 'menu' ? (
          <MenuManagerPage auth={auth} />
        ) : activeTab === 'kiosk' ? (
          <KioskOrderPage auth={auth} />
        ) : (
          <SettingsPage auth={auth} />
        )}
        <div className="py-2 text-center">
          <span className="text-[10px] text-gray-600">Powered by AccTech Kiosk</span>
        </div>
      </div>
    </div>
  );
};

export default App;
